import { Alert, Box, Button, Divider, Flex, Group, Table, Text } from '@mantine/core';
import {
  IconArrowRight,
  IconDatabaseEdit,
  IconPencil,
  IconTrash,
  IconUserExclamation,
  IconPlus,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ClientDataOperationView, ClientDataProposedAction } from '../chat/types';
import { ProposalDecidedLine } from './ProposalDecidedLine';

type Props = {
  proposal: ClientDataProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

const OPERATION_ICONS = {
  CREATE: IconPlus,
  UPDATE: IconPencil,
  DELETE: IconTrash,
} as const;

const OPERATION_NOUNS = {
  CREATE: 'addition',
  UPDATE: 'update',
  DELETE: 'deletion',
} as const;

function humaniseKey(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Before/after values can be nested structures (e.g. address blocks or contribution lists)
// dumped straight from the backend models; render one level of them readably and summarise
// anything deeper rather than showing raw JSON.
function formatValue(value: unknown, depth = 0): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const hasNestedItems = value.some(item => typeof item === 'object' && item !== null);
    if (depth > 1 || hasNestedItems) {
      return value.length === 1 ? '1 item' : `${value.length} items`;
    }
    return value.map(item => formatValue(item, depth + 1)).join(', ');
  }
  if (typeof value !== 'object') return String(value);
  const entries = Object.entries(value).filter(
    ([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== '',
  );
  if (entries.length === 0) return '—';
  if (depth > 0) {
    return entries.length === 1 ? '1 field' : `${entries.length} fields`;
  }
  return entries
    .map(([key, entryValue]) => `${humaniseKey(key)}: ${formatValue(entryValue, depth + 1)}`)
    .join(', ');
}

function FieldChangeRows({ operation }: { operation: ClientDataOperationView }) {
  if (operation.fieldChanges.length === 0) return null;
  return (
    <Table withRowBorders={false} verticalSpacing={2} horizontalSpacing="xs">
      <Table.Tbody>
        {operation.fieldChanges.map(change => (
          <Table.Tr key={change.field}>
            <Table.Td w="35%" p={0} pr="xs" style={{ verticalAlign: 'top' }}>
              <Text size="textXs" c="gray.7">
                {change.label}
              </Text>
            </Table.Td>
            <Table.Td p={0}>
              <Group gap="xxs" align="center" wrap="wrap">
                <Text
                  size="textXs"
                  c="gray.7"
                  td={operation.operation === 'DELETE' ? 'line-through' : undefined}
                  style={{ wordBreak: 'break-word' }}
                >
                  {formatValue(change.before)}
                </Text>
                {operation.operation !== 'DELETE' && (
                  <>
                    <IconArrowRight
                      size={12}
                      stroke={1.5}
                      color="var(--mantine-color-gray-6)"
                      aria-hidden
                    />
                    <Text
                      size="textXs"
                      fw={FONT_WEIGHTS.medium}
                      c="gray.9"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {formatValue(change.after)}
                    </Text>
                  </>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function OperationBlock({
  operation,
  focusedClientId,
}: {
  operation: ClientDataOperationView;
  focusedClientId: string | null;
}) {
  const OperationIcon = OPERATION_ICONS[operation.operation];
  const targetsAnotherMember = focusedClientId !== null && operation.clientId !== focusedClientId;

  return (
    <Flex direction="column" gap="xxs">
      <Group gap="xs" align="center" wrap="nowrap">
        <OperationIcon size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            {operation.entityLabel}
          </Text>
          <Text size="textXs" c="gray.7">
            {operation.entityTypeLabel} · proposed {OPERATION_NOUNS[operation.operation]} · for{' '}
            {operation.clientName}
          </Text>
        </Box>
      </Group>
      {targetsAnotherMember && (
        <Alert
          color="warning"
          variant="light"
          p="xs"
          icon={<IconUserExclamation size={16} stroke={1.5} aria-hidden />}
        >
          <Text size="textXs" c="gray.8">
            This changes {operation.clientName}&apos;s data — not the member you are viewing.
          </Text>
        </Alert>
      )}
      <FieldChangeRows operation={operation} />
    </Flex>
  );
}

export function ClientDataProposalWidget({ proposal, onDecide, disabled }: Props) {
  const isPending = proposal.status === 'PENDING' || !proposal.status;
  const operationCount = proposal.operations.length;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <IconDatabaseEdit
            size={16}
            stroke={1.5}
            color="var(--mantine-color-orange-7)"
            aria-hidden
          />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Proposed data {operationCount === 1 ? 'change' : `changes (${operationCount})`}
          </Text>
        </Group>
        {/* Everything below the summary is computed server-side from the validated proposal,
            never from Siesta's own description of what it did. */}
        <Text size="textSm" c="gray.8">
          {proposal.summary}
        </Text>
        <Flex direction="column" gap="sm">
          {proposal.operations.map((operation, index) => (
            <Flex key={`${operation.entityType}-${index}`} direction="column" gap="sm">
              {index > 0 && <Divider color="orange.2" />}
              <OperationBlock operation={operation} focusedClientId={proposal.focusedClientId} />
            </Flex>
          ))}
        </Flex>
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <ProposalDecidedLine status={proposal.status} failureReason={proposal.failureReason} />
        )}
      </Flex>
    </Box>
  );
}
